import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import PartyDrawer from "@/components/common/PartyDrawer";
import { Button } from "@/components/ui/button";
import { Plus, Building2 } from "lucide-react";
import { useSortableList, SortHeader } from "@/components/common/useSortableList";

export default function Vendors() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [drawer, setDrawer] = useState({ open: false, item: null });
    const [loading, setLoading] = useState(true);

    const { sorted, sortKey, sortDir, onSort } = useSortableList(items, {
        storageKey: "vendors",
        initial: { key: "name", dir: "asc" },
        search,
        searchKeys: ["name", "company", "email", "gstin"],
    });

    const load = async () => {
        setLoading(true);
        const { data } = await api.get("/vendors");
        setItems(data); setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const save = async (form) => {
        if (drawer.item) await api.put(`/vendors/${drawer.item.id}`, form);
        else await api.post(`/vendors`, form);
        await load();
    };

    return (
        <>
            <PageHeader kicker="PURCHASES" title="Vendors" subtitle="Vendor master with GSTIN, GST treatment, multi-currency profile and outstanding balances."
                actions={<Button onClick={() => setDrawer({ open: true, item: null })} data-testid="vendor-new"><Plus className="w-4 h-4 mr-1.5" />New vendor</Button>} />
            <PageBody>
                <FilterBar search={search} onSearch={setSearch} />
                <div className="bg-card border border-border rounded-md overflow-hidden">
                    {loading ? <div className="p-8 text-center text-muted-foreground">Loading…</div> :
                        sorted.length === 0 ? <EmptyState icon={Building2} title="No vendors yet" description="Add your suppliers to start recording bills." actionLabel="New vendor" onAction={() => setDrawer({ open: true, item: null })} /> :
                            <table className="fin-table">
                                <thead><tr>
                                    <SortHeader label="Name" sortKey="name" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="GSTIN" sortKey="gstin" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Currency" sortKey="currency" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Terms" sortKey="payment_terms_days" currentKey={sortKey} dir={sortDir} onSort={onSort} />
                                    <SortHeader label="Outstanding" sortKey="outstanding" currentKey={sortKey} dir={sortDir} onSort={onSort} align="right" />
                                </tr></thead>
                                <tbody>
                                    {sorted.map((c) => (
                                        <tr key={c.id} data-testid={`vendor-row-${c.id}`}>
                                            <td className="cursor-pointer" onClick={() => navigate(`/purchases/vendors/${c.id}`)}>
                                                <div className="font-medium hover:text-gold hover:underline underline-offset-2 decoration-gold/40" data-testid={`vendor-360-link-${c.id}`}>{c.name}</div>
                                                <div className="text-[11.5px] text-muted-foreground">{c.email}</div>
                                            </td>
                                            <td className="font-mono text-[12px] cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{c.gstin || "—"}</td>
                                            <td className="cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{c.currency}</td>
                                            <td className="text-[12px] text-muted-foreground cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{c.payment_terms_label || (c.payment_terms_days ? `Net ${c.payment_terms_days}` : "Due on receipt")}</td>
                                            <td className="num cursor-pointer" onClick={() => setDrawer({ open: true, item: c })}>{fmtMoney(c.outstanding || 0, c.currency)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                </div>
            </PageBody>
            <PartyDrawer
                open={drawer.open}
                onClose={() => setDrawer({ open: false, item: null })}
                party={drawer.item}
                kind="vendor"
                onSave={save}
            />
        </>
    );
}
